import { ComparableBoatData, useGetActiveCarouselItem } from '@components/CompareBoats/hooks/useCompare';
import { Grid } from 'antd';
import { KeenSliderPlugin, useKeenSlider } from 'keen-slider/react';
import { FC, useEffect, useState } from 'react';
import styles from '../CompareTable.module.scss';
import PanelDataItem from './PanelDataItem.component';

interface MainSpecsPanelDataInterface {
  boatComparableData: ComparableBoatData[];
}

const MutationPlugin: KeenSliderPlugin = (slider) => {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      slider.update();
    });
  });

  const config = { childList: true };

  slider.on('created', () => {
    observer.observe(slider.container, config);
  });
  slider.on('destroyed', () => {
    observer.disconnect();
  });
};
const MainSpecsPanelData: FC<MainSpecsPanelDataInterface> = ({ boatComparableData }) => {
  const [_loaded, setLoaded] = useState(false);
  const activeCarouselItem = useGetActiveCarouselItem();

  const { md } = Grid.useBreakpoint();

  const [ref, instanceRef] = useKeenSlider<HTMLDivElement>(
    {
      drag: false,
      slides: {
        perView: 7,
      },
      breakpoints: {
        '(max-width: 1440px)': {
          slides: { perView: 6 },
        },
        '(max-width: 1200px)': {
          slides: { perView: 5 },
        },
        '(max-width: 1024px)': {
          slides: { perView: 4 },
        },
        '(max-width: 992px)': {
          slides: { perView: 3 },
        },
        '(max-width: 768px)': {
          slides: { perView: 2 },
        },
        '(max-width: 520px)': {
          slides: { perView: 2 },
        },
      },
      // slideChanged(slider) {
      //   setActiveCarouselItem(slider.track.details.rel);
      // },
      created() {
        setLoaded(true);
      },
    },
    [MutationPlugin],
  );

  useEffect(() => {
    instanceRef?.current?.moveToIdx(activeCarouselItem);
  }, [activeCarouselItem, instanceRef]);

  const mainSpecsList: { specLabel: string; specId: number }[] = [
    { specLabel: 'Maker', specId: 1 },
    { specLabel: 'Maker website', specId: 2 },
    { specLabel: 'Model', specId: 3 },
    { specLabel: 'First built hull', specId: 4 },
    { specLabel: 'Last built hull', specId: 5 },
    { specLabel: 'Version', specId: 6 },
    { specLabel: 'Price (list price, excl. VAT)', specId: 7 },
    { specLabel: 'Boat type', specId: 8 },
    { specLabel: 'Category', specId: 9 },
    { specLabel: 'Activity', specId: 10 },
    { specLabel: 'Hull type', specId: 11 },
    { specLabel: 'Hull material', specId: 12 },
    { specLabel: 'Cabins', specId: 13 },
    { specLabel: 'Births', specId: 14 },
    { specLabel: 'Heads (WC)', specId: 15 },
  ];

  return (
    <div className={styles.specsPanelDataContainer}>
      <div style={md ? { width: 192 } : { width: '100%' }}>
        {mainSpecsList.map((mainSpecItem) => (
          <div key={mainSpecItem.specId}>
            <div className={styles.specsNamesStyles}>{mainSpecItem.specLabel}</div>
            {mainSpecItem?.specId === 1 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.manufacturer?.name} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 2 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem
                  panelData={boat?.manufacturer?.website ?? undefined}
                  key={boat?._id}
                  panelDataType="link"
                />
              ))}
            {mainSpecItem?.specId === 3 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.model} key={boat?._id} panelDataType="text" />
              ))}

            {mainSpecItem?.specId === 4 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.yearFirst} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 5 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.yearLast} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 6 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.version} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 7 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem
                  panelData={boat?.referencePrice?.amount ?? undefined}
                  key={boat?._id}
                  panelDataType="price"
                  panelDataPriceSymbol={boat?.referencePrice?.currency ?? undefined}
                />
              ))}

            {mainSpecItem?.specId === 8 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem key={boat?._id} panelData={boat?.type?.name} panelDataType="text" />
              ))}

            {mainSpecItem?.specId === 9 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem key={boat?._id} panelData={boat?.categories?.at(0)?.name} panelDataType="text" />
              ))}

            {mainSpecItem?.specId === 10 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem key={boat?._id} panelData={boat?.activities?.at(0)?.name} panelDataType="text" />
              ))}

            {mainSpecItem?.specId === 11 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem key={boat?._id} panelData={boat?.hullType?.name} panelDataType="text" />
              ))}

            {mainSpecItem?.specId === 12 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem key={boat?._id} panelData={boat?.hullMaterial?.name} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 13 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem key={boat?._id} panelData={boat?.cabins} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 14 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem key={boat?._id} panelData={boat?.berths} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 15 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem key={boat?._id} panelData={boat?.heads} panelDataType="text" />
              ))}
          </div>
        ))}
      </div>

      {md && (
        <div ref={ref} className="keen-slider">
          {boatComparableData?.map((boat) => (
            <div key={boat._id} className="keen-slider__slide">
              <PanelDataItem panelData={boat?.manufacturer?.name} panelDataType="paragraph" />

              <PanelDataItem
                panelData={boat?.manufacturer?.website ?? undefined}
                panelDataType="link"
                ellipsisMaxLength={20}
              />

              <PanelDataItem panelData={boat?.model} panelDataType="text" />

              <PanelDataItem panelData={boat?.yearFirst} panelDataType="text" />

              <PanelDataItem panelData={boat?.yearLast} panelDataType="text" />

              <PanelDataItem panelData={boat?.version} panelDataType="text" />

              <PanelDataItem
                panelData={boat?.referencePrice?.amount ?? undefined}
                panelDataType="price"
                panelDataPriceSymbol={boat?.referencePrice?.currency ?? undefined}
              />

              <PanelDataItem panelData={boat?.type?.name} panelDataType="text" />

              <PanelDataItem panelData={boat?.categories?.at(0)?.name} panelDataType="text" />

              <PanelDataItem panelData={boat?.activities?.at(0)?.name} panelDataType="text" />

              <PanelDataItem panelData={boat?.hullType?.name} panelDataType="text" />

              <PanelDataItem panelData={boat?.hullMaterial?.name} panelDataType="text" />

              <PanelDataItem panelData={boat?.cabins} panelDataType="text" />

              <PanelDataItem panelData={boat?.berths} panelDataType="text" />

              <PanelDataItem panelData={boat?.heads} panelDataType="text" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MainSpecsPanelData;
